import { useCallback, useEffect, useRef, useState } from 'react';
import { loadGameData } from '../data/gameData.ts';
import { loadTerrainStyles, terrainStyles } from '../terrain/index.ts';
import { TilesetTool } from '../terrain/TilesetTool.tsx';
import type { LevelDoc } from '../model/format.ts';
import { PlayView } from './PlayView.tsx';
import { useEditor, findObject, draftObject, type Panel } from './store.ts';
import { Viewport } from './Viewport.tsx';
import { installLobbySync, requestStop, startLobby } from '../lobby/client.ts';
import { LobbyBar, LobbyCursors, LobbyDialog } from '../lobby/ui.tsx';
import { startAutosave } from './autosave.ts';
import { AccessDialog } from './Access.tsx';
import { HistoryDialog } from './History.tsx';
import { SoundProfilesDialog } from './Sounds.tsx';
import { HelpDialog, OpenDialog, SettingsDialog, ToolOptions, Toolbar, TopBar, openLevelById, saveCurrent } from './Chrome.tsx';
import { Inspector } from './Inspector.tsx';
import { LayersPanel, LevelPanel, ObjectsPanel, TilesPanel } from './panels.tsx';
import { deleteSelection, duplicateObjects, findTerrain, moveBody } from './actions.ts';
import { bodyBounds, objectInfo } from './scene.ts';
import { ringBounds } from './polygon.ts';
import { Icon } from './widgets.tsx';
import { CaseFixer } from './CaseFixer.tsx';
import { IdeasDock } from './Ideas.tsx';
import { TileEditorHost } from './pixel/TileEditor.tsx';
import { useSettings } from './settings.ts';

const SIDE_MIN = 260, SIDE_DEFAULT = 340, DOUBLE_MS = 400;
/** room the viewport keeps next to the side panel */
const STAGE_MIN = 360;

/** Drag handle on the side panel's left edge; double-click goes back to the default width. */
function SideResizer() {
  const drag = useRef<{ x: number; w: number; el: HTMLElement; id: number } | null>(null);
  // dblclick is unreliable with pointer capture and on touch: a second tap within DOUBLE_MS resets
  const lastTap = useRef(0);
  const clamp = (w: number) => Math.round(Math.max(SIDE_MIN, Math.min(w, window.innerWidth - STAGE_MIN)));
  const end = (e: React.PointerEvent) => {
    const g = drag.current;
    if (!g || g.id !== e.pointerId) return;
    drag.current = null;
    document.body.classList.remove('side-resizing');
    const moved = Math.abs(e.clientX - g.x) > 3;
    const now = performance.now();
    if (!moved && now - lastTap.current < DOUBLE_MS) {
      lastTap.current = 0;
      g.el.style.setProperty('--side-w', `${SIDE_DEFAULT}px`);
      useSettings.getState().update({ sideWidth: SIDE_DEFAULT });
      return;
    }
    lastTap.current = moved ? 0 : now;
    useSettings.getState().update({ sideWidth: clamp(g.w + g.x - e.clientX) });
  };
  return (
    <div
      className="side-resizer"
      title="Drag to resize the panel (double-click: default width)"
      onPointerDown={(e) => {
        const el = (e.currentTarget as HTMLElement).parentElement!;
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { x: e.clientX, w: el.getBoundingClientRect().width, el, id: e.pointerId };
        document.body.classList.add('side-resizing');
        e.preventDefault();
      }}
      onPointerMove={(e) => {
        const g = drag.current;
        if (g && g.id === e.pointerId) g.el.style.setProperty('--side-w', `${clamp(g.w + g.x - e.clientX)}px`);
      }}
      onPointerUp={end}
      onPointerCancel={end}
    />
  );
}

const PANELS: { id: Panel; label: string; icon: string }[] = [
  { id: 'inspector', label: 'Inspect', icon: 'settings' },
  { id: 'layers', label: 'Layers', icon: 'layers' },
  { id: 'tiles', label: 'Tiles', icon: 'tileset' },
  { id: 'objects', label: 'Objects', icon: 'object' },
  { id: 'level', label: 'Level', icon: 'folder' },
];

export function App() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [directPlay, setDirectPlay] = useState<LevelDoc | null>(null);
  const s = useEditor();
  const sideWidth = useSettings((x) => x.sideWidth);

  useEffect(() => {
    (async () => {
      const gd = await loadGameData();
      await loadTerrainStyles(gd.base);
      const st = useEditor.getState();
      st.set({ polygon: { ...st.polygon, style: terrainStyles()[0]?.id ?? '' } });
      const params = new URLSearchParams(location.search);
      const play = params.get('play');
      if (play) {
        const entry = gd.project.levels.find((l) => l.id === play) ?? gd.project.levels[0];
        setDirectPlay(await (await fetch(`${gd.base}/${entry.file}`)).json());
        setReady(true);
        return;
      }
      const level = params.get('level');
      let last: { id: string; builtin: boolean } | null = null;
      try {
        last = JSON.parse(localStorage.getItem('ple:last') ?? 'null');
      } catch {
        /* ignore */
      }
      if (level) await openLevelById(level, !!gd.project.levels.find((l) => l.id === level) && params.get('saved') !== '1');
      else if (last) await openLevelById(last.id, last.builtin);
      if (!useEditor.getState().doc) useEditor.getState().set({ modal: 'open' });
      setReady(true);
    })().catch((e) => setError(String(e?.message ?? e)));
  }, []);

  useEffect(() => startAutosave(), []);
  useEffect(() => startLobby(), []);
  useEffect(() => installLobbySync(), []);
  useEffect(() => {
    localStorage.setItem('ple:panelOpen', s.panelOpen ? '1' : '0');
  }, [s.panelOpen]);

  // global editor commands
  useEffect(() => {
    const save = () => saveCurrent();
    const del = () => {
      const st = useEditor.getState();
      if (st.selection.kind !== 'none') deleteSelection(st.selection);
    };
    const dup = () => {
      const st = useEditor.getState();
      if (st.selection.kind === 'objects') st.set({ selection: { kind: 'objects', ids: duplicateObjects(st.selection.ids) } });
    };
    const focus = () => {
      const st = useEditor.getState();
      const doc = st.doc;
      if (!doc) return;
      let b: [number, number, number, number] | null = null;
      const sel = st.selection;
      if (sel.kind === 'objects') {
        const f = findObject(doc, sel.ids[0]);
        if (f) b = objectInfo(doc, f.obj).bounds;
      } else if (sel.kind === 'body') {
        const body = doc.bodies.find((x) => x.id === sel.id);
        const bb = body && bodyBounds(doc, body);
        if (body && bb) b = [bb[0] + body.origin[0], bb[1] + body.origin[1], bb[2] + body.origin[0], bb[3] + body.origin[1]];
      } else if (sel.kind === 'terrain') {
        const t = findTerrain(doc, sel.id);
        if (t) b = ringBounds(t.terrain.rings[0]);
      }
      if (b) st.set({ camera: { ...st.camera, x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2 } });
    };
    const nudge = (e: Event) => {
      const st = useEditor.getState();
      const { key, big } = (e as CustomEvent).detail as { key: string; big: boolean };
      const step = big ? 1 : 0.5;
      const dx = key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0;
      const dy = key === 'arrowdown' ? -step : key === 'arrowup' ? step : 0;
      const sel = st.selection;
      if (sel.kind === 'objects') {
        st.change('Nudge', (d) => {
          for (const id of sel.ids) {
            const o = draftObject(d, id);
            if (o) { o.x += dx; o.y += dy; }
          }
        });
      } else if (sel.kind === 'body') {
        st.change('Nudge body', (d) => moveBody(d, sel.id, Math.sign(dx), Math.sign(dy)));
      } else {
        const z = st.camera.zoom;
        st.set({ camera: { ...st.camera, x: st.camera.x + (dx * 200) / z, y: st.camera.y + (dy * 200) / z } });
      }
    };
    window.addEventListener('ple:save', save);
    window.addEventListener('ple:delete', del);
    window.addEventListener('ple:duplicate', dup);
    window.addEventListener('ple:focus', focus);
    window.addEventListener('ple:nudge', nudge);
    return () => {
      window.removeEventListener('ple:save', save);
      window.removeEventListener('ple:delete', del);
      window.removeEventListener('ple:duplicate', dup);
      window.removeEventListener('ple:focus', focus);
      window.removeEventListener('ple:nudge', nudge);
    };
  }, []);

  const exitPlay = useCallback(() => {
    if (requestStop()) return;
    useEditor.getState().set({ playing: null });
  }, []);
  const exitDirect = useCallback(() => setDirectPlay(null), []);

  if (error) return <div className="loading">Failed to load: {error}</div>;
  if (!ready) return <div className="loading">Loading game data…</div>;
  if (directPlay) return <PlayView doc={directPlay} onExit={exitDirect} />;

  return (
    <div className={`app ${s.panelOpen ? 'panel-open' : ''}`}>
      <TopBar />
      <div className="main">
        <Toolbar />
        <div className="stage">
          {s.doc ? <Viewport /> : <div className="loading">No level open</div>}
          <LobbyCursors />
          <div className="stage-top">
            <LobbyBar />
            <ToolOptions />
          </div>
          {s.tool === 'fix' && s.doc && <CaseFixer />}
          <IdeasDock />
          {s.toast && <div className="toast">{s.toast}</div>}
          <TileEditorHost />
        </div>
        <aside className={`side ${s.panelOpen ? 'open' : ''}`} style={{ '--side-w': `${sideWidth}px` } as React.CSSProperties}>
          {s.panelOpen && <SideResizer />}
          <div className="tabs">
            <button className="tab panel-toggle" title={s.panelOpen ? 'Collapse panel (Tab)' : 'Expand panel (Tab)'} onClick={() => s.set({ panelOpen: !s.panelOpen })}>
              <Icon name={s.panelOpen ? 'collapse' : 'expand'} size={18} />
            </button>
            {PANELS.map((p) => (
              <button key={p.id} className={`tab ${s.panel === p.id && s.panelOpen ? 'active' : ''}`} onClick={() => s.set(s.panel === p.id && s.panelOpen ? { panelOpen: false } : { panel: p.id, panelOpen: true })}>
                <Icon name={p.icon} size={18} />
                <span>{p.label}</span>
              </button>
            ))}
          </div>
          {s.panelOpen && s.doc && (
            <div className="panel">
              {s.panel === 'inspector' && <Inspector />}
              {s.panel === 'layers' && <LayersPanel />}
              {s.panel === 'tiles' && <TilesPanel />}
              {s.panel === 'objects' && <ObjectsPanel />}
              {s.panel === 'level' && <LevelPanel />}
            </div>
          )}
        </aside>
      </div>
      {s.modal === 'open' && <OpenDialog />}
      {s.modal === 'access' && <AccessDialog />}
      {s.modal === 'lobby' && <LobbyDialog />}
      {s.modal === 'help' && <HelpDialog />}
      {s.modal === 'settings' && <SettingsDialog />}
      {s.modal === 'history' && <HistoryDialog />}
      {s.modal === 'sounds' && <SoundProfilesDialog />}
      {s.modal === 'tileset' && (
        <div className="overlay">
          <TilesetTool initialStyle={s.tilesetStyle ?? undefined} onClose={() => s.set({ modal: null })} />
        </div>
      )}
      {s.playing && s.doc && (
        <div className="overlay play">
          <PlayView doc={s.doc} spawn={s.playing.spawn} onExit={exitPlay} />
        </div>
      )}
    </div>
  );
}
